import React from 'react';
import Icon from '../../../components/AppIcon';

const DocumentTree = ({ documents, selectedDocument, onDocumentSelect }) => {
  const getIconForType = (type) => {
    const iconMap = {
      pdd: 'FileText',
      monitoring: 'BarChart3',
      final_report: 'FileCode',
      gee_report: 'Map',
      validation: 'CheckCircle',
      verification: 'Shield',
      methodology: 'BookOpen',
      stakeholder: 'Users',
      other: 'File'
    };
    return iconMap?.[type] || 'File';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'indexed':
        return 'var(--color-success)';
      case 'processing':
        return 'var(--color-warning)';
      case 'failed':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      indexed: 'Approved',
      processing: 'In Review',
      failed: 'Pending'
    };
    return labels?.[status] || status;
  };

  const groupedDocuments = documents?.reduce((acc, doc) => {
    const type = doc?.type || 'other';
    if (!acc?.[type]) acc[type] = [];
    acc?.[type]?.push(doc);
    return acc;
  }, {});

  const typeLabels = {
    pdd: 'Project Design Documents',
    monitoring: 'Monitoring Reports',
    validation: 'Validation Documents',
    verification: 'Verification Documents',
    methodology: 'Methodologies',
    stakeholder: 'Stakeholder Documents',
    other: 'Other Documents'
  };

  if (!documents || documents?.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Icon name="FolderOpen" size={48} className="mx-auto mb-3 opacity-20" />
        <p className="text-sm">No documents found</p>
      </div>
    );
  }

  return (
    <div className="p-2">
      {Object?.entries(groupedDocuments)?.map(([type, docs]) => (
        <div key={type} className="mb-4">
          <div className="flex items-center gap-2 px-2 py-1 mb-1">
            <Icon name={getIconForType(type)} size={16} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {typeLabels?.[type] || type}
            </span>
            <span className="text-xs text-muted-foreground">({docs?.length})</span>
          </div>
          
          {docs?.map(doc => (
            <button
              key={doc?.id}
              onClick={() => onDocumentSelect?.(doc)}
              className={`
                w-full p-3 rounded-lg text-left transition-all duration-200
                ${selectedDocument?.id === doc?.id
                  ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted border border-transparent'
                }
              `}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex items-center justify-center w-8 h-8 rounded flex-shrink-0"
                  style={{ backgroundColor: `${getStatusColor(doc?.status)}15` }}
                >
                  <Icon name={getIconForType(doc?.type)} size={16} color={getStatusColor(doc?.status)} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground truncate mb-1">
                    {doc?.name}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span
                      className="px-1.5 py-0.5 rounded caption"
                      style={{
                        backgroundColor: `${getStatusColor(doc?.status)}15`,
                        color: getStatusColor(doc?.status)
                      }}
                    >
                      {getStatusLabel(doc?.status)}
                    </span>
                    <span>{doc?.metadata?.version || 'v1.0'}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default DocumentTree;